import type { VisualizationSpec } from "vega-embed";
import { markColorsPicked } from "./chartColorPick";

type SpecObject = Record<string, unknown>;

/**
 * A Vega-Lite color encoding is either a static single color ({ value: "#..." })
 * or a field mapped through a scale ({ field, scale: { range/scheme } }).
 * Chart Studio's palette can only repaint whichever shape is already there —
 * it never invents a field to color by.
 */
function recolorColorEncoding(colorEncoding: SpecObject, colors: string[]): SpecObject {
  if ("field" in colorEncoding) {
    const scale = (colorEncoding.scale as SpecObject) ?? {};
    const { scheme: previousScheme, ...explicitScale } = scale;
    return { ...colorEncoding, scale: { ...explicitScale, range: colors } };
  }
  if ("value" in colorEncoding) {
    return { ...colorEncoding, value: colors[0] };
  }
  return colorEncoding;
}

function recolorEncoding(
  encoding: SpecObject | undefined,
  colors: string[],
): SpecObject | undefined {
  if (!encoding || !encoding.color) return encoding;
  return { ...encoding, color: recolorColorEncoding(encoding.color as SpecObject, colors) };
}

const NON_SERIES_MARKS = new Set(["text", "rule", "tick", "image"]);
const COLOR_CHANNELS = ["color", "fill", "stroke"];

/** A single-series layer or unit has no colour channel; its mark's own colour takes the palette's first colour. */
function recolorMark(view: SpecObject, colors: string[]): SpecObject {
  const mark = view.mark;
  const type =
    typeof mark === "string" ? mark : mark && typeof mark === "object" && typeof (mark as SpecObject).type === "string" ? ((mark as SpecObject).type as string) : "";
  if (!type || NON_SERIES_MARKS.has(type)) return view;
  const encoding = (view.encoding as SpecObject | undefined) ?? {};
  if (COLOR_CHANNELS.some((key) => key in encoding)) return view;
  return { ...view, mark: { ...(typeof mark === "object" ? (mark as SpecObject) : {}), type, color: colors[0] } };
}

/**
 * Repaints a chart's spec with a new palette on the client, with no backend
 * round trip — the widget's spec in local state is swapped for this
 * function's return value, and react-vega re-renders with the new colors on
 * the next pass. Only unit specs and layer specs are handled, matching the
 * two shapes ChartWidget's own sizing pass (toResponsiveSpec) already
 * supports for Chart Studio charts.
 */
export function applyPaletteToSpec(
  spec: VisualizationSpec,
  colors: string[],
): VisualizationSpec {
  if (colors.length === 0) return spec;
  const s = spec as SpecObject;
  if (Array.isArray(s.layer)) {
    const layers = (s.layer as SpecObject[]).map((layer): SpecObject => {
      const marked = recolorMark(layer, colors);
      return { ...marked, encoding: recolorEncoding(layer.encoding as SpecObject | undefined, colors) };
    });
    const changed = layers.some(
      (layer, index) => layer.encoding !== (s.layer as SpecObject[])[index].encoding || layer.mark !== (s.layer as SpecObject[])[index].mark,
    );
    const repainted = { ...s, layer: layers } as VisualizationSpec;
    return changed ? markColorsPicked(repainted) : repainted;
  }
  const marked = recolorMark(s, colors);
  const encoding = recolorEncoding(s.encoding as SpecObject | undefined, colors);
  const repainted = { ...marked, encoding } as VisualizationSpec;
  return encoding === s.encoding && marked === s ? repainted : markColorsPicked(repainted);
}
